import { useState } from 'react';
import {
  BanknotesIcon, GlobeAltIcon, LockClosedIcon, MapPinIcon, ShieldCheckIcon, EnvelopeIcon, PhoneIcon, TruckIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';
import { useMarket } from '../../context/MarketContext';
import useStoreInfo from '../../hooks/useStoreInfo';

const QUICK_LINKS  = [['Shop All','/shop'],['New Arrivals','/collections/new-arrivals'],['Brands','/brands'],['Track Order','/track-order'],['Contact Us','/contact'],['About Us','/about']];
const POLICY_LINKS = [['Return & Refund','/return-policy'],['Shipping & Delivery','/shipping'],['Cancellation Policy','/cancellation'],['Privacy Policy','/privacy-policy'],['Terms & Conditions','/terms']];

const SOCIALS = [
  {
    label: 'Instagram',
    href: 'https://instagram.com/skinnthrive',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://facebook.com/skinnthrive',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    label: 'Twitter / X',
    href: 'https://x.com/skinnthrive',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    label: 'YouTube',
    href: 'https://youtube.com/@skinnthrive',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
  },
];

const PAYMENTS_GLOBAL = ['VISA', 'MC'];
const PAYMENTS_INDIA  = ['UPI', 'NB', 'COD', 'EMI'];

/* Mobile accordion group */
const AccordionGroup = ({ title, links }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        className="flex items-center justify-between w-full py-3.5 text-left text-sm font-semibold text-white"
        onClick={() => setOpen(!open)}
      >
        {title}
        <svg className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      {open && (
        <ul className="pb-3 space-y-2.5">
          {links.map(([label, to]) => (
            <li key={to}>
              <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors">{label}</Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Footer = () => {
  const contact = useStoreInfo();   // e-mail, phone, address from the admin (Settings → General)
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { currentMarket } = useMarket();
  const isIndia = currentMarket?.isDefault ?? true;
  const payments = isIndia ? [...PAYMENTS_GLOBAL, ...PAYMENTS_INDIA] : PAYMENTS_GLOBAL;

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await apiClient.post('/contact', { name: email, email, subject: 'Newsletter Subscription', message: 'Please subscribe me to the newsletter.' });
      toast.success('Subscribed! We\'ll keep you updated.');
      setEmail('');
    } catch {
      toast.error('Could not subscribe right now — please try again.');   // it used to claim success after a failure
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer style={{ background: '#111111' }} className="text-white">

      {/* Trust strip */}
      <div style={{ background: 'rgba(107,33,168,0.2)' }} className="border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              ...(isIndia ? [{ Icon: BanknotesIcon, title: 'Cash on Delivery', sub: 'Available pan-India' }] : [{ Icon: GlobeAltIcon, title: 'International Delivery', sub: 'Fast & reliable shipping' }]),
              { Icon: LockClosedIcon,  title: 'Secured Shopping',      sub: '256-bit SSL encrypted' },
              { Icon: TruckIcon,       title: 'Live Order Tracking',   sub: 'Real-time updates' },
              { Icon: ShieldCheckIcon, title: '100% Authentic',        sub: 'Verified & sealed' },
            ].map(({ Icon, ...b }) => (
              <div key={b.title} className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-white/80" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{b.title}</p>
                  <p className="text-xs text-white/50">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Brand col */}
          <div>
            <div className="mb-5">
              <div className="text-2xl font-black tracking-tight text-white">SKINN</div>
              <div className="text-[0.5rem] font-bold tracking-[0.35em] text-accent -mt-0.5 uppercase">Thrive</div>
            </div>
            <p className="text-sm text-white/60 mb-5 leading-relaxed">
              Your trusted destination for authentic dermatologist-recommended skincare, haircare &amp; wellness.
            </p>
            <div className="flex gap-2">
              {SOCIALS.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60
                             hover:border-primary hover:bg-primary hover:text-white transition-all"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Quick Links</h3>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors inline-block">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Policies</h3>
            <ul className="space-y-2.5">
              {POLICY_LINKS.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors inline-block">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Stay Updated</h3>
            <p className="text-sm text-white/60 mb-4 leading-relaxed">
              Exclusive deals, skincare tips &amp; new launches — straight to your inbox.
            </p>
            <form onSubmit={handleNewsletter} className="flex gap-2 mb-5">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5
                           text-sm text-white placeholder-white/40
                           focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30
                           transition-all"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors shrink-0 disabled:opacity-60"
              >
                {submitting ? '…' : 'Go'}
              </button>
            </form>
            <div className="text-sm text-white/60 space-y-1.5">
              <p className="flex items-center gap-2"><EnvelopeIcon className="w-4 h-4 shrink-0" aria-hidden="true" />{contact.email}</p>
              {contact.phone && <p className="flex items-center gap-2"><PhoneIcon className="w-4 h-4 shrink-0" aria-hidden="true" />{contact.phone}</p>}
              {contact.address && <p className="flex items-start gap-2"><MapPinIcon className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />{contact.address}</p>}
            </div>
          </div>
        </div>

        {/* Mobile accordion */}
        <div className="md:hidden mb-8">
          <div className="mb-6">
            <div className="text-xl font-black tracking-tight text-white">SKINN</div>
            <div className="text-[0.45rem] font-bold tracking-[0.35em] text-accent -mt-0.5 uppercase">Thrive</div>
            <p className="text-xs text-white/50 mt-2 leading-relaxed">Authentic dermatologist-recommended skincare &amp; wellness products.</p>
            <div className="flex gap-2 mt-4">
              {SOCIALS.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-primary hover:border-primary hover:text-white transition-all">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
          <AccordionGroup title="Quick Links"  links={QUICK_LINKS} />
          <AccordionGroup title="Policies"     links={POLICY_LINKS} />
          <div className="pt-4">
            <p className="text-sm font-semibold text-white mb-2">Stay Updated</p>
            <form onSubmit={handleNewsletter} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-primary"
              />
              <button type="submit" disabled={submitting} className="bg-primary text-white px-4 rounded-lg text-sm font-semibold disabled:opacity-60">{submitting ? '…' : 'Go'}</button>
            </form>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40 order-2 md:order-1">
            © {new Date().getFullYear()} Skinn Thrive. All rights reserved.
          </p>
          <div className="flex items-center gap-2 flex-wrap justify-center order-1 md:order-2">
            <span className="text-xs text-white/40 mr-1">We accept:</span>
            {payments.map(p => (
              <span key={p} className="bg-white/10 text-white/70 text-[10px] font-bold px-2.5 py-1 rounded border border-white/10">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
